import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

// Enterprise subdomain tree
import { resolveSubdomainFromHostname, ADMIN_SUBDOMAIN } from "@/lib/subdomain";
import { useAuth } from "@/contexts/AuthContext";
import { EnterpriseProvider, useEnterprise } from "@/contexts/EnterpriseContext";
import { EnterpriseBrandingProvider } from "@/components/enterprise/EnterpriseBrandingProvider";
import { EnterpriseRequireMembership } from "@/components/enterprise/EnterpriseRequireMembership";
import { EnterpriseDashboardPage } from "@/pages/enterprise/enterprise-dashboard-page";
import { EnterpriseSettingsPage } from "@/pages/enterprise/enterprise-settings-page";
import { EnterpriseStaffMembersPage } from "@/pages/enterprise/enterprise-staff-members-page";
import { EnterpriseStaffRequestsPage } from "@/pages/enterprise/enterprise-staff-requests-page";
import { EnterpriseStaffRequestDetailPage } from "@/pages/enterprise/enterprise-staff-request-detail-page";
import { EnterpriseLoginPage } from "@/pages/enterprise/enterprise-login-page";

import { AppLayout } from "@/components/shared/AppLayout";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { LiveChatBridge } from "@/components/shared/LiveChatBridge";
import { PortalDocumentTitle } from "@/components/shared/PortalDocumentTitle";
import { PortalThemeProvider } from "@/components/shared/PortalThemeProvider";
import { ProtectedRoute } from "@/components/shared/ProtectedRoute";
import { RootRedirect } from "@/components/shared/RootRedirect";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { TenantSwitcherProvider } from "@/contexts/TenantSwitcherContext";
import { LoginPage } from "@/pages/auth/login-page";
import { RegisterPage } from "@/pages/auth/register-page";
import { VerifyPage } from "@/pages/auth/verify-page";
import { VerifyRootInvitePage } from "@/pages/auth/verify-root-invite-page";
import { VerifyEmailPage } from "@/pages/auth/verify-email-page";
import { SsoCallbackPage } from "@/pages/auth/sso-callback-page";
import { NameEntryPage } from "@/pages/auth/name-entry-page";
import { CompleteProfilePage } from "@/pages/auth/complete-profile-page";
import { AdminLoginPage } from "@/pages/auth/admin-login-page";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password-page";
import { ResetPasswordPage } from "@/pages/auth/reset-password-page";
import { SetInitialPasswordPage } from "@/pages/auth/set-initial-password-page";
import { ProfessionalInviteRoute } from "@/pages/auth/professional-invite-route";
import { DlocalReturnPage } from "@/pages/pay/dlocal-return-page";
import { BankTransferPage } from "@/pages/pay/bank-transfer-page";
import { MercadoPagoPaymentPage } from "@/pages/pay/mercadopago-page";
import { ProfileEditPage } from "@/pages/profile/profile-edit-page";
import { UserAddressesPage } from "@/pages/profile/user-addresses-page";
import { ProfilePage } from "@/pages/profile/profile-page";
import { UsersPage } from "@/pages/users/users-page";
import { BlogEditorPage } from "@/pages/platform/blog-editor-page";
import { BlogListPage } from "@/pages/platform/blog-list-page";
import { CertificatePricingPage } from "@/pages/platform/certificate-pricing-page";
import { CertificateProductEditPage } from "@/pages/platform/certificate-product-edit-page";
import { CertificateProductNewPage } from "@/pages/platform/certificate-product-new-page";
import { CertificateRequestIntentsPage } from "@/pages/platform/certificate-request-intents-page";
import { QuoteRequestsTriagePage } from "@/pages/platform/quote-requests-triage-page";
import { PlatformQuoteRequestDetailPage } from "@/pages/platform/platform-quote-request-detail-page";
import { PlatformProfessionalPayoutDetailPage } from "@/pages/platform/platform-professional-payout-detail-page";
import { PlatformProfessionalPayoutsPage } from "@/pages/platform/platform-professional-payouts-page";
import { PlatformModerationFlagsPage } from "@/pages/platform/platform-moderation-flags-page";
import { PlatformBankTransfersPage } from "@/pages/platform/platform-bank-transfers-page";
import { EnterprisesListPage } from "@/pages/platform/enterprises-list-page";
import { EnterpriseNewPage } from "@/pages/platform/enterprise-new-page";
import { EnterpriseDetailPage } from "@/pages/platform/enterprise-detail-page";
import { EnterpriseProductBuilderPage } from "@/pages/platform/enterprise-product-builder-page";
import { PlatformAnalyticsPage } from "@/pages/platform/analytics-page";
import { AcceptEnterpriseInvitePage } from "@/pages/auth/accept-enterprise-invite-page";
import { AcceptRequestInvitePage } from "@/pages/auth/accept-request-invite-page";
import { FeedbackPage } from "@/pages/feedback/feedback-page";
import { RatePage } from "@/pages/feedback/rate-page";
import { RequestRatingsPage } from "@/pages/feedback/request-ratings-page";
import { RequestRatingDetailPage } from "@/pages/feedback/request-rating-detail-page";
import {
  RequestsDetailRoute,
  RequestsListRoute,
  RequestsNewWizardRoute,
} from "@/components/shared/RequestsRoute";
import { ProfessionalsPage } from "@/pages/professionals/professionals-page";
import { ProfessionalDetailRoute } from "@/pages/professionals/professional-detail-route";
import { NotaryDetailPage } from "@/pages/notaries/notary-detail-page";
import { AccountantDetailPage } from "@/pages/accountants/accountant-detail-page";
import { AccountantPendingReviewDetailPage } from "@/pages/accountants/accountant-pending-review-detail-page";
import { ProfessionsPage } from "@/pages/platform/professions-page";
import { ProfessionalEarningsPage } from "@/pages/notary/professional-earnings-page";
import { ProfessionalPayoutRequestPage } from "@/pages/notary/professional-payout-request-page";
import { TwoFactorSetupPage } from "@/pages/security/two-factor-setup-page";
import { NotificationSettingsPage } from "@/pages/profile/notification-settings-page";

function RedirectLegacyAccountantPendingReviewDetail(): JSX.Element {
  const { inviteId } = useParams<{ inviteId: string }>();
  if (!inviteId?.trim()) {
    return <Navigate to="/accountants" replace />;
  }
  return (
    <Navigate
      to={`/accountants/invites/${encodeURIComponent(inviteId)}/review`}
      replace
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

// ---------------------------------------------------------------------------
// Enterprise subdomain router (mounted when hostname is a tenant subdomain)
// ---------------------------------------------------------------------------

/**
 * Redirects to app.gestionalo.uy when the enterprise subdomain is not found.
 * Must be rendered inside EnterpriseProvider.
 */
function EnterpriseNotFoundRedirect({ children }: { children: React.ReactNode }): JSX.Element {
  const { loading, notFound } = useEnterprise();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  if (notFound) {
    const { protocol, host } = window.location;
    const hostWithoutPort = host.split(":")[0];
    const port = host.includes(":") ? `:${host.split(":")[1]}` : "";
    const parts = hostWithoutPort.split(".");
    const baseDomain = parts.length >= 2 ? parts.slice(-2).join(".") : hostWithoutPort;
    window.location.href = `${protocol}//app.${baseDomain}${port}/`;
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  return <>{children}</>;
}

function EnterpriseRootRouter({ subdomain }: { subdomain: string }): JSX.Element {
  const { isAuthenticated } = useAuth();
  return (
    <EnterpriseProvider subdomain={subdomain} isAuthenticated={isAuthenticated}>
      <EnterpriseBrandingProvider>
        <EnterpriseNotFoundRedirect>
        <BrowserRouter>
          <PortalThemeProvider>
            <Toaster
              position="top-center"
              containerStyle={{ top: 16, zIndex: 99999 }}
              toastOptions={{
                success: {
                  className:
                    "border border-green-200 bg-green-50 text-green-900 dark:border-green-800/80 dark:bg-green-950/50 dark:text-green-100",
                },
                error: {
                  className:
                    "border border-red-200 bg-red-50 text-red-900 dark:border-red-800/80 dark:bg-red-950/50 dark:text-red-100",
                },
              }}
            />
            <TooltipProvider delayDuration={250}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <EnterpriseRequireMembership>
                      <AppLayout />
                    </EnterpriseRequireMembership>
                  }
                >
                  <Route index element={<EnterpriseDashboardPage />} />
                  <Route
                    path="members"
                    element={
                      <EnterpriseRequireMembership>
                        <EnterpriseStaffMembersPage />
                      </EnterpriseRequireMembership>
                    }
                  />
                  <Route
                    path="requests"
                    element={
                      <EnterpriseRequireMembership>
                        <EnterpriseStaffRequestsPage />
                      </EnterpriseRequireMembership>
                    }
                  />
                  <Route
                    path="requests/:id"
                    element={
                      <EnterpriseRequireMembership>
                        <EnterpriseStaffRequestDetailPage />
                      </EnterpriseRequireMembership>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <EnterpriseRequireMembership requireAdmin>
                        <EnterpriseSettingsPage />
                      </EnterpriseRequireMembership>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="profile/edit" element={<ProfileEditPage />} />
                  <Route
                    path="profile/notifications"
                    element={<NotificationSettingsPage />}
                  />
                </Route>
                <Route path="/login" element={<EnterpriseLoginPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </TooltipProvider>
          </PortalThemeProvider>
        </BrowserRouter>
      </EnterpriseNotFoundRedirect>
      </EnterpriseBrandingProvider>
    </EnterpriseProvider>
  );
}

// ---------------------------------------------------------------------------
// Admin subdomain router (mounted when hostname is admin.*)
// ---------------------------------------------------------------------------

function AdminRootRouter(): JSX.Element {
  return (
    <BrowserRouter>
      <PortalThemeProvider>
        <Toaster
          position="top-center"
          containerStyle={{ top: 16, zIndex: 99999 }}
          toastOptions={{
            success: {
              className:
                "border border-green-200 bg-green-50 text-green-900 dark:border-green-800/80 dark:bg-green-950/50 dark:text-green-100",
            },
            error: {
              className:
                "border border-red-200 bg-red-50 text-red-900 dark:border-red-800/80 dark:bg-red-950/50 dark:text-red-100",
            },
          }}
        />
        <TooltipProvider delayDuration={250}>
          <Routes>
            <Route path="/login" element={<AdminLoginPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/platform/analytics" replace />} />
              <Route path="/platform/enterprises" element={<EnterprisesListPage />} />
              <Route path="/platform/enterprises/new" element={<EnterpriseNewPage />} />
              <Route path="/platform/enterprises/:enterpriseId" element={<EnterpriseDetailPage />} />
              <Route path="/platform/enterprises/:enterpriseId/products/new" element={<EnterpriseProductBuilderPage />} />
              <Route path="/platform/enterprises/:enterpriseId/products/:productId/edit" element={<EnterpriseProductBuilderPage />} />
              <Route path="/requests" element={<RequestsListRoute />} />
              <Route path="/requests/:id" element={<RequestsDetailRoute />} />
              <Route path="/profesionales" element={<ProfessionalsPage />} />
              <Route path="/profesionales/:id" element={<ProfessionalDetailRoute />} />
              <Route path="/platform/professions" element={<ProfessionsPage />} />
              {/*
                THE OLD PATHS STILL RESOLVE (control C-4). They are in
                bookmarks, in sent emails and in muscle memory, and a section
                that disappears reads as a broken deploy rather than a moved
                one. The detail paths keep their own pages: the id is the same
                id, and sending them to the new dispatcher would be a second
                lookup for no gain.
              */}
              <Route path="/notaries" element={<Navigate to="/profesionales" replace />} />
              <Route path="/notaries/:id" element={<NotaryDetailPage />} />
              <Route path="/accountants" element={<Navigate to="/profesionales" replace />} />
              <Route path="/accountants/:id" element={<AccountantDetailPage />} />
              <Route path="/accountants/invites/:inviteId/review" element={<AccountantPendingReviewDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/products" element={<CertificatePricingPage />} />
              <Route path="/products/new" element={<CertificateProductNewPage />} />
              <Route path="/products/:productId" element={<CertificateProductEditPage />} />
              <Route path="/certificate-request-intents" element={<CertificateRequestIntentsPage />} />
              <Route path="/platform/quote-requests" element={<QuoteRequestsTriagePage />} />
              <Route path="/platform/quote-requests/:id" element={<PlatformQuoteRequestDetailPage />} />
              <Route path="/professional-payouts" element={<PlatformProfessionalPayoutsPage />} />
              <Route path="/moderation-flags" element={<PlatformModerationFlagsPage />} />
              <Route path="/platform/bank-transfers" element={<PlatformBankTransfersPage />} />
              <Route path="/professional-payouts/:id" element={<PlatformProfessionalPayoutDetailPage />} />
              <Route path="/platform/analytics" element={<PlatformAnalyticsPage />} />
              <Route path="/platform/blog" element={<BlogListPage />} />
              <Route path="/platform/blog/new" element={<BlogEditorPage />} />
              <Route path="/platform/blog/:id" element={<BlogEditorPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/ratings" element={<RequestRatingsPage />} />
              <Route path="/ratings/:id" element={<RequestRatingDetailPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/edit" element={<ProfileEditPage />} />
              <Route path="/profile/notifications" element={<NotificationSettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </TooltipProvider>
      </PortalThemeProvider>
    </BrowserRouter>
  );
}

/**
 * WHERE AN UNKNOWN PATH GOES — one element, two routes.
 *
 * It is the catch-all's element AND the invite dispatcher's fallback, because
 * `/:professionInviteSlug` is a dynamic single-segment route and therefore
 * outranks `path="*"` for every one-segment path in the app. A typo that used
 * to reach the catch-all now reaches the dispatcher, and the only way it
 * behaves identically is by rendering the same thing — hence a shared
 * component rather than two copies that can drift (FM-2).
 */
function UnknownPathRedirect(): JSX.Element {
  return (
    <ProtectedRoute>
      <TenantSwitcherProvider>
        <Navigate to="/" replace />
      </TenantSwitcherProvider>
    </ProtectedRoute>
  );
}

// ---------------------------------------------------------------------------
// App — resolves hostname first, then branches
// ---------------------------------------------------------------------------

function App(): JSX.Element {
  const subdomain = resolveSubdomainFromHostname(window.location.hostname);

  if (subdomain === ADMIN_SUBDOMAIN) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AdminRootRouter />
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  if (subdomain !== null) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <EnterpriseRootRouter subdomain={subdomain} />
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  // Default (non-enterprise) app tree — unchanged
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PortalDocumentTitle />
        <BrowserRouter>
        <PortalThemeProvider>
        <Toaster
          position="top-center"
          containerStyle={{ top: 16, zIndex: 99999 }}
          toastOptions={{
            success: {
              className:
                "border border-green-200 bg-green-50 text-green-900 dark:border-green-800/80 dark:bg-green-950/50 dark:text-green-100",
            },
            error: {
              className:
                "border border-red-200 bg-red-50 text-red-900 dark:border-red-800/80 dark:bg-red-950/50 dark:text-red-100",
            },
          }}
        />
        <LiveChatBridge />
        <TooltipProvider delayDuration={250}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/verify" element={<VerifyPage />} />
          <Route path="/auth/verify-root-invite" element={<VerifyRootInvitePage />} />
          <Route
            path="/verify-email"
            element={
              <ProtectedRoute>
                <VerifyEmailPage />
              </ProtectedRoute>
            }
          />
          <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/auth/onboarding/name" element={<NameEntryPage />} />
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute>
                <CompleteProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/onboarding/set-password"
            element={
              <ProtectedRoute>
                <SetInitialPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accept-enterprise-invite"
            element={<AcceptEnterpriseInvitePage />}
          />
          <Route
            path="/accept-request-invite"
            element={<AcceptRequestInvitePage />}
          />
          {/*
            INVITATION ACCEPTANCE, for every profession in the registry.

            These two replace four routes named after two professions. A
            profession added to the registry produced a working invitation
            link that no route here matched, so a real professional clicking
            it was redirected away from their own signup; the dispatcher
            resolves the invitation by token and renders the page its
            verification strategy asks for (decision a78bb4f).

            THE SEGMENT IS WHOLE. React Router 6.26 cannot express
            `/accept-:profession-invitation` — a partial dynamic segment
            matches nothing and fails silently — so the shape is checked
            inside the component.

            The fallback is the catch-all's own element, because a dynamic
            single-segment route now outranks `path="*"` for every
            one-segment path in the app and every typo must still land where
            it always did.
          */}
          <Route
            path="/:professionInviteSlug"
            element={<ProfessionalInviteRoute fallback={<UnknownPathRedirect />} />}
          />
          <Route
            path="/:professionInviteSlug/:token"
            element={<ProfessionalInviteRoute fallback={<UnknownPathRedirect />} />}
          />
          <Route path="/pay/dlocal-return" element={<DlocalReturnPage />} />
          <Route path="/pay/bank-transfer" element={<BankTransferPage />} />
          <Route path="/pay/mercadopago" element={<MercadoPagoPaymentPage />} />
          <Route path="/rate/:token" element={<RatePage />} />
          {/* Guest-checkout: the wizard route mounts outside <ProtectedRoute> and
              applies its own auth-aware shell (PublicWizardLayout for anonymous,
              AppLayout for authenticated). See RequestsRoute.tsx::RequestsNewWizardRoute. */}
          <Route path="/requests/new" element={<RequestsNewWizardRoute />} />
          <Route
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <AppLayout />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          >
            <Route path="/requests" element={<RequestsListRoute />} />
            <Route path="/requests/:id" element={<RequestsDetailRoute />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<ProfileEditPage />} />
            {/* The address book. Reachable by everyone with an account rather
                than gated to consumers: a professional has addresses too, and
                a route that 404s for half the users is worse than a sidebar
                entry only some of them see. */}
            <Route path="/addresses" element={<UserAddressesPage />} />
            <Route path="/profile/notifications" element={<NotificationSettingsPage />} />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-requests"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <Navigate to="/requests" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-requests/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <Navigate to="/requests" replace />
                </ProtectedRoute>
              }
            />
            {/*
              THE SAME SECTION IN THE SECOND ROUTE TREE. This file has two:
              the admin shell above, and this one. A change applied to only
              one of them is how the section behaves differently depending on
              which host the admin reached it from.
            */}
            <Route
              path="/profesionales"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <ProfessionalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profesionales/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <ProfessionalDetailRoute />
                </ProtectedRoute>
              }
            />
            {/*
              THE REGISTRY ITSELF, in the second route tree as well. App.tsx
              has two, and a section mounted in only one behaves differently
              depending on which host the admin reached it from — the property
              professionals-section.spec.ts was written to hold.
            */}
            <Route
              path="/platform/professions"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <ProfessionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notaries/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <NotaryDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notaries"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <Navigate to="/profesionales" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accountants/pending-reviews/:inviteId"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <RedirectLegacyAccountantPendingReviewDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accountants/pending-reviews"
              element={<Navigate to="/accountants" replace />}
            />
            <Route
              path="/accountants/invites/:inviteId/review"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <AccountantPendingReviewDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accountants/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <AccountantDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accountants"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <Navigate to="/profesionales" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-pricing"
              element={<Navigate to="/products" replace />}
            />
            <Route
              path="/products/new"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificateProductNewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:productId"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificateProductEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificatePricingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-request-intents"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificateRequestIntentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/quote-requests"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <QuoteRequestsTriagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/quote-requests/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformQuoteRequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/earnings"
              element={
                <ProtectedRoute allowedRoles={["professional"]}>
                  <ProfessionalEarningsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/earnings/request-payout"
              element={
                <ProtectedRoute allowedRoles={["professional"]}>
                  <ProfessionalPayoutRequestPage />
                </ProtectedRoute>
              }
            />
            {/* Two-factor setup. Reachable by an UN-enrolled professional on
                purpose — the backend's enrollment endpoints are deliberately
                outside the gate that demands enrollment, and this route is the
                client half of that. Gating it on being enrolled would be the
                same deadlock (spec 5d9018f9, FM-5). */}
            <Route
              path="/security/two-factor"
              element={
                <ProtectedRoute allowedRoles={["professional"]}>
                  <TwoFactorSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/professional-payouts"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformProfessionalPayoutsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/moderation-flags"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformModerationFlagsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/professional-payouts/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformProfessionalPayoutDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/enterprises"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <EnterprisesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/enterprises/new"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <EnterpriseNewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/enterprises/:enterpriseId"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <EnterpriseDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/enterprises/:enterpriseId/products/new"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <EnterpriseProductBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/enterprises/:enterpriseId/products/:productId/edit"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <EnterpriseProductBuilderPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <RootRedirect />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<UnknownPathRedirect />} />
        </Routes>
        </TooltipProvider>
        </PortalThemeProvider>
      </BrowserRouter>
    </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
